import { Controller } from "@hotwired/stimulus"

// Keeps the review screen honest about what it is going to do.
//
// Every activity we already have starts on "spring over", and a row bound for
// a place we have no address for becomes a draft rather than a published
// activity — both of which the administrator can change from the page. So the
// answer to "what is new, what already exists, what is incomplete" moves as
// they work. This walks the rows on every change and rewrites the state of
// each card, the running count, and the button label.
const STATES = {
  create:  { label: "Oprettes",            badge: "ad-badge ad-badge--green" },
  draft:   { label: "Oprettes som kladde", badge: "ad-badge ad-badge--amber" },
  extend:  { label: "Forlænges",           badge: "ad-badge ad-badge--brand" },
  update:  { label: "Får ny tekst",        badge: "ad-badge ad-badge--brand" },
  replace: { label: "Erstatter den gamle", badge: "ad-badge ad-badge--amber" },
  skip:    { label: "Springes over",       badge: "ad-badge" }
}

export default class extends Controller {
  static targets = ["summary", "submit", "total"]

  connect() {
    this.refresh()
  }

  refresh() {
    const counts = { create: 0, draft: 0, extend: 0, update: 0, replace: 0, skip: 0 }

    this.element.querySelectorAll("[data-import-row]").forEach((row) => {
      const state = this.stateOf(row)
      counts[state] += 1
      row.dataset.state = state

      const pill = row.querySelector("[data-import-state]")
      if (pill) {
        pill.textContent = STATES[state].label
        pill.className = STATES[state].badge
      }
    })

    if (this.hasTotalTarget) this.totalTarget.textContent = this.newCount(counts)
    if (this.hasSummaryTarget) this.summaryTarget.textContent = this.summaryFor(counts)
    if (this.hasSubmitTarget) this.updateSubmit(counts)
  }

  // Mirrors Activity#state. A row is only a draft while it would create a place
  // we do not have: pick an existing one from the dropdown, or type an address,
  // and it is an ordinary new activity again.
  stateOf(row) {
    const checked = row.querySelector('input[type=radio][name$="[action]"]:checked')
    const action = checked && checked.value in STATES ? checked.value : "skip"
    if (action !== "create") return action

    const address = row.querySelector("[data-import-address]")
    const location = row.querySelector('select[name$="[location_id]"]')
    const unplaced = address && address.value.trim() === "" && (!location || location.value === "")

    return unplaced ? "draft" : "create"
  }

  newCount({ create, draft }) {
    return create + draft
  }

  // Mirrors CalendarImportHelper#calendar_import_plan_summary.
  summaryFor(counts) {
    const parts = [`${this.activities(this.newCount(counts) + counts.replace)} bliver oprettet`]
    if (counts.draft > 0) parts.push(`${counts.draft} som kladde uden sted`)
    if (counts.replace > 0) parts.push(`${this.series(counts.replace)} erstatter en gammel`)
    if (counts.extend > 0) parts.push(`${this.series(counts.extend)} bliver forlænget`)
    if (counts.update > 0) parts.push(`${counts.update} får ny tekst`)
    if (counts.skip > 0) parts.push(`${counts.skip} bliver sprunget over`)
    return parts.join(", ") + "."
  }

  updateSubmit(counts) {
    const fresh = this.newCount(counts)
    const label =
      counts.update + counts.replace > 0 ? "Gennemfør importen" :
      fresh > 0 ? `Opret ${this.activities(fresh)}` :
      counts.extend > 0 ? `Forlæng ${this.series(counts.extend)}` :
      "Opret aktiviteterne"

    this.submitTarget.value = label
    this.submitTarget.disabled = fresh + counts.extend + counts.update + counts.replace === 0
    this.submitTarget.dataset.turboConfirm = `${this.summaryFor(counts)} Vil du fortsætte?`
    // Rails stamped data-disable-with from the server-rendered label; without
    // this the button reverts to the old number the moment it is pressed.
    this.submitTarget.dataset.disableWith = label
  }

  activities(n) { return n === 1 ? "1 ny aktivitet" : `${n} nye aktiviteter` }
  series(n)     { return n === 1 ? "1 serie" : `${n} serier` }
}
